import { Avatar, Group, Tooltip } from '@mantine/core';
import { IconEye, IconLock, IconWorld } from '@tabler/icons-react';
import { apiBaseUrl } from '@ayocore/platform/client/helpers/fetcher';
import { getUserInitials } from '@ayocore/platform/client/helpers/userInitials';
import { useUsersController } from '@ayocore/platform/identity/client/UsersController';
import { DRIVE_ACCESS_LABEL, type EffectiveAccess } from './driveAccess';
import { groupInitial, zoneSubjectTooltip, type ZoneSubject } from './zoneSubjects';

/** Абсолютный src аватарки: относительные URL дополняем базой сервера (dev-режим). */
function resolveAvatarSrc(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.startsWith('http') ? url : `${apiBaseUrl}${url}`;
}

/**
 * ZoneOwnerAvatar — аватарка владельца зоны (дерево: у public_read-папок после
 * значка «глазик» — сразу видно, кто может туда записывать). Данные — кеш
 * UsersController, без запросов.
 */
export function ZoneOwnerAvatar({ userId, size }: { userId: number; size: number | string }) {
  const { getUser } = useUsersController();
  const cached = getUser(userId);
  const label = cached?.name || `#${userId}`;
  return (
    <Tooltip label={`Владелец: ${label}`} withArrow>
      <Avatar
        src={resolveAvatarSrc(cached?.avatarThumbUrl)}
        size={size}
        radius='xl'
        color='indigo'
        variant='filled'
      >
        {getUserInitials(label)}
      </Avatar>
    </Tooltip>
  );
}

/**
 * Чип группы: квадрат с первой буквой названия, розовый.
 *
 * Отличается от человека ТРЕМЯ признаками сразу — формой (квадрат против круга),
 * цветом и буквой вместо инициалов. Одного цвета мало: в дереве аватарка 18px,
 * а различать субъекты надо мгновенно.
 */
function ZoneGroupChip({ subject, size }: { subject: ZoneSubject; size: number | string }) {
  return (
    // multiline: вторая строка тултипа — первые участники группы
    <Tooltip label={zoneSubjectTooltip(subject)} withArrow multiline>
      <Avatar size={size} radius='sm' color='pink' variant='filled'>
        {groupInitial(subject.name)}
      </Avatar>
    </Tooltip>
  );
}

/** Аватарка человека: фото из кеша UsersController, фоллбек — инициалы. */
function ZoneUserAvatar({ subject, size }: { subject: ZoneSubject; size: number | string }) {
  const { getUser } = useUsersController();
  const cached = getUser(subject.id);
  const label = cached?.name || subject.name;
  return (
    <Tooltip label={zoneSubjectTooltip({ ...subject, name: label })} withArrow>
      {/* color/variant работают только для фоллбека-инициалов (фото перекрывает) */}
      <Avatar
        src={resolveAvatarSrc(cached?.avatarThumbUrl)}
        size={size}
        radius='xl'
        color='indigo'
        variant='filled'
      >
        {getUserInitials(label)}
      </Avatar>
    </Tooltip>
  );
}

/**
 * ZoneSubjectAvatars — стопка субъектов custom-зоны: группы и люди (чистое
 * представление; данные и порядок — useZoneMembersMap/sortZoneSubjects).
 * Сверх max — «+N» с перечислением скрытых в тултипе.
 */
export function ZoneSubjectAvatars({
  subjects,
  max,
  size,
}: {
  subjects: ZoneSubject[];
  /** Сколько субъектов показывать до сворачивания в «+N». */
  max: number;
  /** Размер аватарки: токен Mantine или px. */
  size: number | string;
}) {
  if (subjects.length === 0) return null;

  const shown = subjects.slice(0, max);
  const hidden = subjects.slice(max);

  return (
    // spacing — величина наезда (отрицательный отступ): меньше значение — меньше перекрытие
    <Avatar.Group spacing={6}>
      {shown.map(subject =>
        subject.kind === 'group' ? (
          <ZoneGroupChip key={`group-${subject.id}`} subject={subject} size={size} />
        ) : (
          <ZoneUserAvatar key={`user-${subject.id}`} subject={subject} size={size} />
        )
      )}
      {hidden.length > 0 && (
        <Tooltip label={hidden.map(zoneSubjectTooltip).join('\n')} withArrow multiline>
          <Avatar size={size} radius='xl' color='indigo' variant='light'>
            +{hidden.length}
          </Avatar>
        </Tooltip>
      )}
    </Avatar.Group>
  );
}

/** Чип «Все» — зона открыта всем авторизованным (значок тот же, что в дереве). */
function ZoneEveryoneChip({ size }: { size: number | string }) {
  return (
    <Tooltip label={`Все: ${DRIVE_ACCESS_LABEL.public_write}`} withArrow>
      <Avatar size={size} radius='xl' color='blue' variant='filled'>
        <IconWorld size={14} />
      </Avatar>
    </Tooltip>
  );
}

/**
 * DriveZoneMembers — индикатор тех, кто имеет отношение к АКТИВНОЙ зоне (ряд
 * крошек, по правому краю). Показывает что-то при любом режиме, чтобы блок
 * читался как ответ на вопрос «кто здесь», а не пропадал:
 *
 * - custom — стопка субъектов ACL (группы, затем люди);
 * - public_write — чип «Все»;
 * - public_read — «глазик + владелец» (кто один может писать);
 * - private — «замок + владелец».
 *
 * Состав custom-зоны видит любой её участник (кто видит папку — тот минимум
 * read): прозрачность по модели Google Drive, серверный гейт — getAccessEntries.
 */
export function DriveZoneMembers({
  effective,
  membersById,
}: {
  effective: EffectiveAccess | null;
  membersById: Map<number, ZoneSubject[]>;
}) {
  if (effective?.access === 'custom') {
    const subjects = membersById.get(effective.rootId) ?? [];
    return <ZoneSubjectAvatars subjects={subjects} max={6} size='sm' />;
  }
  if (effective?.access === 'public_write') {
    return <ZoneEveryoneChip size='sm' />;
  }
  if (effective?.access === 'public_read' && effective.ownerId != null) {
    return (
      <Group gap={4} wrap='nowrap'>
        <IconEye
          size={14}
          color='var(--mantine-color-orange-6)'
          title={DRIVE_ACCESS_LABEL.public_read}
        />
        <ZoneOwnerAvatar userId={effective.ownerId} size='sm' />
      </Group>
    );
  }
  if (effective?.access === 'private' && effective.ownerId != null) {
    return (
      <Group gap={4} wrap='nowrap'>
        <IconLock
          size={14}
          color='var(--mantine-color-grape-6)'
          title={DRIVE_ACCESS_LABEL.private}
        />
        <ZoneOwnerAvatar userId={effective.ownerId} size='sm' />
      </Group>
    );
  }
  return null;
}
